import { useTranslation } from 'react-i18next'

import { ClubCrest } from '@/components/ui-kit/ClubCrest'
import { LockedButton } from '@/components/ui-kit/Buttons'
import { ProgressBar } from '@/components/ui-kit/Primitives'
import { ScreenShell, TopBar } from '@/components/ui-kit/Shell'
import { de } from '@/lib/format'
import { grossPayout, payout, tax } from '@/lib/game'
import { cn } from '@/lib/utils'
import type { MatchOutcome, PhoneMatch } from '@/types/game'

const OUTCOMES: MatchOutcome[] = ['home', 'draw', 'away']

/**
 * Die Auflösung eines Spiels.
 *
 * Gezeigt wird genau das Spiel, das die Lehrkraft gerade aufgedeckt hat - es
 * gibt bewusst keine Blätterfunktion. Wer vorspulen könnte, würde der Klasse
 * die Spannung nehmen, und die Ergebnisse der späteren Spiele liegen dem Handy
 * ohnehin noch gar nicht vor.
 */
export function RevealScreen({
    matches,
    revealIndex,
}: {
    matches: PhoneMatch[]
    revealIndex: number
}) {
    const { t } = useTranslation()
    const match = matches[revealIndex]

    if (!match) {
        return null
    }

    const skipped = match.pick === null || match.pick === 'skip'
    const hit = !skipped && match.pick === match.result

    return (
        <ScreenShell>
            <TopBar title={t('reveal.title')} right={`${revealIndex + 1}/${matches.length}`} />

            <ProgressBar value={revealIndex + 1} total={matches.length} className="mt-3" />

            <div className="flex flex-1 flex-col justify-center gap-5">
                <p className="text-center text-[11px] font-bold tracking-[.1em] text-wb-dim uppercase">
                    {t('reveal.matchOf', { current: revealIndex + 1, total: matches.length })}
                </p>

                <div className="flex items-center justify-center gap-4">
                    <div className="flex flex-1 flex-col items-center gap-1.5">
                        <ClubCrest club={match.home} size={52} />
                        <span className="text-center text-xs font-bold text-balance">
                            {match.home.name}
                        </span>
                    </div>

                    <div
                        className="wb-display wb-num text-4xl"
                        data-testid="score"
                        data-revealed={match.revealed}
                    >
                        {match.score ? `${match.score.home}:${match.score.away}` : '–:–'}
                    </div>

                    <div className="flex flex-1 flex-col items-center gap-1.5">
                        <ClubCrest club={match.away} size={52} />
                        <span className="text-center text-xs font-bold text-balance">
                            {match.away.name}
                        </span>
                    </div>
                </div>

                <div className="flex gap-2">
                    {OUTCOMES.map((outcome) => {
                        const isResult = match.revealed && outcome === match.result
                        const isPick = !skipped && outcome === match.pick

                        return (
                            <div
                                key={outcome}
                                className={cn(
                                    // Gleiche Regel wie beim Tippen (OddsChip): eine Zeile,
                                    // gleich breite Kaesten, Schrift schrumpft erst, wenn es eng wird
                                    '@container min-w-0 flex-1 rounded-xl border-[1.5px] px-2 py-2.5 text-center',
                                    // WETTBANDIT-63: Das ausgefuellte Feld ist der eigene
                                    // Tipp, nicht das Ergebnis. Vorher leuchtete das Ergebnis
                                    // gruen - viele hielten das fuer ihren eigenen Tipp und
                                    // sahen nicht, ob sie richtig lagen. Das Ergebnis bekommt
                                    // deshalb nur noch einen gruenen Rand.
                                    !isResult &&
                                        !isPick &&
                                        'border-wb-border bg-wb-panel-lo text-wb-text',
                                    isResult &&
                                        !isPick &&
                                        'border-wb-win bg-wb-panel-lo text-wb-text',
                                    // Vor der Aufdeckung steht noch kein Treffer fest - der
                                    // eigene Tipp ist dann nur hervorgehoben, nicht bewertet.
                                    isPick &&
                                        !match.revealed &&
                                        'border-wb-accent bg-wb-panel-lo text-wb-text',
                                    isPick &&
                                        match.revealed &&
                                        hit &&
                                        'border-wb-win bg-wb-win text-[#0a0d14]',
                                    isPick &&
                                        match.revealed &&
                                        !hit &&
                                        'border-wb-loss bg-wb-loss text-[#0a0d14]',
                                )}
                                data-testid={isPick ? 'reveal-own-pick' : undefined}
                            >
                                <div className="text-[length:min(10px,10.8cqw)] font-bold whitespace-nowrap uppercase opacity-65">
                                    {t(`bet.odds.${outcome}`)}
                                </div>
                                <div className="mt-0.5 wb-num text-lg font-extrabold">
                                    {de(match.odds[outcome])}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <section
                    className={cn(
                        'rounded-2xl border px-4 py-3.5 text-center',
                        skipped && 'border-wb-border bg-wb-panel',
                        !skipped && hit && 'border-wb-win/40 bg-wb-win/10',
                        !skipped && !hit && 'border-wb-loss/40 bg-wb-loss/10',
                    )}
                    data-testid="own-result"
                >
                    <p className="text-[11px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('reveal.yourBet')}
                    </p>

                    {/* Worauf getippt wurde. Ohne diese Zeile sah man nur den
                        gruenen Ergebnis-Chip und darunter "Daneben" - und musste
                        raten, was man selbst gewaehlt hatte. Im Clickdummy steht
                        der eigene Tipp an genau dieser Stelle. */}
                    {!skipped && (
                        <p
                            data-testid="own-tip"
                            className={cn(
                                'mt-1 text-sm font-extrabold',
                                hit ? 'text-wb-win' : 'text-wb-loss',
                            )}
                        >
                            {t(`bet.odds.${match.pick as MatchOutcome}`)}
                        </p>
                    )}
                    <p
                        className={cn(
                            'mt-1 text-lg font-extrabold',
                            skipped && 'text-wb-dim',
                            !skipped && hit && 'text-wb-win',
                            !skipped && !hit && 'text-wb-loss',
                        )}
                    >
                        {skipped ? t('reveal.skipped') : hit ? t('reveal.won') : t('reveal.lost')}
                    </p>
                    <p className="mt-1 wb-num text-sm text-wb-dim">
                        {skipped
                            ? t('reveal.noStake')
                            : hit
                              ? `${t('reveal.payout')}: ${de(payout(match.odds[match.pick as MatchOutcome]))} €`
                              : `${t('reveal.lostStake')}: −10,00 €`}
                    </p>

                    {/* Ricarda hat genau hier nachgefragt, ob die Wettsteuer
                        schon abgezogen ist - man sah es der Zahl nicht an. Die
                        Rechnung steht deshalb daneben: Was die Quote verspricht,
                        was davon abgeht, und was uebrig bleibt. */}
                    {hit && (
                        <p
                            data-testid="reveal-tax"
                            className="mt-0.5 wb-num text-[11px] text-wb-muted"
                        >
                            {t('reveal.taxBreakdown', {
                                gross: de(grossPayout(match.odds[match.pick as MatchOutcome])),
                                tax: de(tax(match.odds[match.pick as MatchOutcome])),
                            })}
                        </p>
                    )}
                </section>
            </div>

            {/* Sagt, wer den Takt vorgibt: Das Handy wartet nicht auf "irgendwas",
                sondern auf die Lehrkraft vorne. Wortlaut aus dem Clickdummy. */}
            <LockedButton>
                {revealIndex < matches.length - 1
                    ? t('reveal.teacherReveals', {
                          current: revealIndex + 1,
                          total: matches.length,
                      })
                    : t('reveal.lastMatch')}
            </LockedButton>
        </ScreenShell>
    )
}
